import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatQuantity } from '@/format/money'
import {
  bulkDelete,
  createView,
  deleteView,
  exportList,
  fetchColumns,
  fetchCount,
  fetchGroups,
  fetchPage,
  fetchViews,
  makeViewDefault,
  saveBlob,
  updateView,
} from './api'
import { ColumnPicker } from './ColumnPicker'
import { DataTable } from './DataTable'
import { FilterBuilder } from './FilterBuilder'
import { GroupRows } from './GroupedRows'
import { ImportPanel } from './ImportPanel'
import { fetchImportTargets } from './importApi'
import { FilterTokens, QuickChips, SearchBox } from './ListToolbar'
import { Pager } from './Pager'
import { Popover } from './Popover'
import { availableChips, localToday, screenOf, searchColumn, type DerivedColumn } from './quickFilters'
import { StateBox } from './StateBox'
import { DEFAULT_PAGE_SIZE, listQueryKey, useListState, type ListStateApi } from './useListState'
import { ViewPicker } from './ViewPicker'
import { TAGGABLE } from './tagsApi'
import { SegmentBar, TagBulkActions } from './TagTools'
import { REASSIGNABLE, SalesBulkActions } from './SalesBulkActions'
import { cardDescriptor } from '@/card/descriptors'
import type {
  ColumnDescriptor,
  ListPage,
  ListRow,
  ListViewDefinition,
  SavedView,
} from './types'
import { columnLabel, groupable } from './columnLabel'
import '@/styles/lists.css'

/**
 * Экран настраиваемого списка (ТЗ §5.1; дизайн: Deals, CompData).
 *
 * Один компонент на все сущности: колонки, фильтры и права приходят с сервера, поэтому
 * список сделок и список товаров отличаются только кодом сущности. Отдельный экран на
 * каждую означал бы, что новый фильтр надо добавлять в семи местах и в шести из них о нём
 * забудут. То, чем сущности всё-таки различаются на экране — быстрые чипы, колонка поиска,
 * группировка по умолчанию, — объявлено конфигом в `quickFilters.ts`, а не ветками здесь.
 *
 * ## Раскладка (дизайн: «Над таблицей один ряд»)
 *
 * Заголовок со счётчиком; один ряд тулбара — представление, поиск, быстрые чипы, «Фильтр»
 * с числом условий, группировка, колонки и «⋯» (выгрузка, импорт, сегменты); под ним —
 * применённые условия токенами. Инструменты администратора не стоят рядами над таблицей:
 * у менеджера с одной строкой данных над ней было четыре ряда управления. При выделении
 * строк тулбар сменяется панелью массовых действий.
 *
 * ## Пагинация
 *
 * Курсорная: «← Назад · Строки 51–100 из N · Вперёд →». Номеров страниц нет — номер это
 * OFFSET под другим именем, и девятисотая страница стоила бы в девятьсот раз дороже первой.
 */

interface EntityListPageProps {
  /** Код сущности в каталоге сервера, например `sales.deal`. */
  entity: string
  /** Ресурс RBAC — для проверки прав на массовые действия и выгрузку. */
  resource: string
  titleKey: string
  /**
   * Состояние списка, поднятое наружу (тикет #71).
   *
   * По умолчанию список владеет им сам — так работают семнадцать разделов из
   * восемнадцати. Сделки переключаются между доской и таблицей, и обе формы обязаны
   * делить один фильтр: состояние, живущее внутри таблицы, умирает вместе с её
   * размонтированием, и фильтр приходится набирать заново при каждом переключении — то
   * есть переключаться перестают.
   */
  listState?: ListStateApi
  /**
   * Дополнительные элементы шапки — например переключатель доска/таблица.
   *
   * Отдельным узлом, а не флагом «показать переключатель»: список не должен знать, что
   * над ним есть доска, иначе каждая следующая форма представления добавляла бы ему по
   * условию.
   */
  toolbar?: React.ReactNode
}

/** Сущности, у которых есть экран создания (`<путь>/new`): контрагент (#70), сделка (#100), лид (#101), товар (V60). */
const CREATABLE = new Set(['sales.counterparty', 'sales.deal', 'sales.lead', 'sales.product', 'hr.leave', 'warehouse.stocktake', 'warehouse.receipt', 'warehouse.transfer'])

/** Сколько групп показывать: сервер отдаёт до пятисот, а заголовок каждой — запрос. */
const MAX_GROUPS = 50
/** Сколько групп раскрыто при открытии — остальные по клику. */
const OPEN_GROUPS = 3

type Tool = 'import' | 'segments' | null

export function EntityListPage({
  entity,
  resource,
  titleKey,
  listState,
  toolbar,
}: EntityListPageProps) {
  const { t, user, locale } = useApp()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const ownState = useListState()
  // Хук зовётся всегда — правило хуков не даёт вызвать его по условию, — а используется
  // переданное состояние, если оно есть.
  const list = listState ?? ownState
  const screen = screenOf(entity)
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [appliedDefinition, setAppliedDefinition] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tool, setTool] = useState<Tool>(null)
  const [viewsOpen, setViewsOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const descriptorQuery = useQuery({
    queryKey: ['list-columns', entity],
    queryFn: ({ signal }) => fetchColumns(entity, signal),
    // Каталог колонок меняется вместе с кодом, то есть не в течение сессии.
    staleTime: 30 * 60 * 1000,
    retry: false,
  })

  const descriptor = descriptorQuery.data

  // Группировка по умолчанию (остатки — по складу) ставится один раз, когда стало видно,
  // что колонка есть в описании: колонка за полевым правом до клиента не доезжает, и
  // группировка по ней отказала бы 422 на открытии. Только у своего состояния — поднятое
  // наружу принадлежит владельцу экрана.
  const defaultsApplied = useRef(false)
  useEffect(() => {
    if (defaultsApplied.current || listState || !descriptor) return
    defaultsApplied.current = true
    const preset = screen.defaultGroupBy
    const column = descriptor.columns.find((candidate) => candidate.name === preset)
    if (preset && column && groupable(column) && ownState.state.groupBy === null) {
      ownState.setGroupBy(preset)
    }
  }, [descriptor, listState, ownState, screen.defaultGroupBy])

  const definition = useMemo<ListViewDefinition>(
    () => ({
      ...list.definition,
      // Пустой список колонок означает «по умолчанию»: их знает сервер, и повторять
      // здесь его выбор значит однажды разойтись с ним.
      columns: list.state.columns.length > 0 ? list.state.columns : (descriptor?.defaultColumns ?? []),
    }),
    [descriptor, list.definition, list.state.columns],
  )
  const grouped = Boolean(list.state.groupBy)

  // Стек курсоров: «Вперёд» кладёт курсор следующей страницы, «Назад» снимает его. Стек
  // привязан к определению запроса — курсор выдан под конкретную сортировку и фильтр, и
  // подставленный к другим он молча пропустил бы строки. Поэтому при смене определения
  // стек не сбрасывается эффектом (между отрисовками ушёл бы запрос со старым курсором),
  // а просто перестаёт считаться действующим.
  const definitionKey = normalise(definition)
  const [paging, setPaging] = useState<{ key: string; stack: (string | null)[] }>({
    key: '',
    stack: [null],
  })
  const stack = paging.key === definitionKey ? paging.stack : [null]
  const cursor = stack[stack.length - 1] ?? null
  const pageSize = definition.pageSize ?? DEFAULT_PAGE_SIZE

  const page = useQuery<ListPage & { index: number }>({
    queryKey: [...listQueryKey(entity, definition), cursor],
    // Сгруппированный список читает строки по группам (GroupRows), плоская страница ему
    // не нужна.
    enabled: Boolean(descriptor) && !grouped,
    // Номер страницы едет вместе с её строками: пока грузится следующая, на экране
    // остаются строки текущей, и диапазон обязан описывать их.
    queryFn: ({ signal }) =>
      fetchPage(entity, definition, cursor, signal).then((data) => ({
        ...data,
        index: stack.length - 1,
      })),
    placeholderData: keepPreviousData,
    retry: false,
  })

  // Общее число — один запрос на фильтр, а не на страницу: count(*) стоит полного прохода.
  const total = useQuery({
    queryKey: ['list', entity, 'count', definition.filters],
    enabled: Boolean(descriptor),
    queryFn: ({ signal }) => fetchCount(entity, definition, signal),
    staleTime: 60 * 1000,
    retry: false,
  })

  const groupsQuery = useQuery({
    queryKey: ['list-groups', entity, definition],
    enabled: Boolean(descriptor) && grouped,
    queryFn: ({ signal }) => fetchGroups(entity, definition, signal),
    retry: false,
  })

  const viewsQuery = useQuery({
    queryKey: ['views', entity],
    queryFn: ({ signal }) => fetchViews(entity, signal),
    retry: false,
  })

  // Тот же ключ, что у ImportPanel: пункт «Импорт…» показывается там, где сервер импорт
  // примет, а не ведёт в пустую панель.
  const importTargets = useQuery({
    queryKey: ['import-targets'],
    queryFn: ({ signal }) => fetchImportTargets(signal),
    retry: false,
  })
  const importable = (importTargets.data ?? []).some((target) => target.key === entity)

  // ------------------------------------------------------ колонки, считаемые на клиенте

  const serverColumns = page.data?.columns ?? definition.columns
  const derived = useMemo<DerivedColumn[]>(
    () =>
      (screen.derived ?? []).filter(
        (column) =>
          serverColumns.includes(column.after) &&
          column.from.every((name) => serverColumns.includes(name)),
      ),
    [screen.derived, serverColumns],
  )
  const columns = useMemo(() => {
    const result = [...serverColumns]
    derived.forEach((column) => result.splice(result.indexOf(column.after) + 1, 0, column.name))
    return result
  }, [derived, serverColumns])
  const descriptors = useMemo<ColumnDescriptor[]>(
    () => [
      ...(descriptor?.columns ?? []),
      ...derived.map((column) => ({
        name: column.name,
        type: 'number' as const,
        // Считается на клиенте — сортировка и фильтр по ней работали бы по странице, а не
        // по складу, поэтому их нет.
        sortable: false,
        filterable: false,
      })),
    ],
    [derived, descriptor],
  )
  const enrich = useCallback(
    (row: ListRow): ListRow => {
      if (derived.length === 0) return row
      const next: ListRow = { ...row }
      derived.forEach((column) => {
        next[column.name] = column.compute(row)
      })
      return next
    },
    [derived],
  )
  const columnClass = useMemo(() => {
    const result: Record<string, string> = {}
    ;(screen.mono ?? []).forEach((name) => (result[name] = 'mono'))
    ;(screen.strong ?? []).forEach((name) => (result[name] = 'strong'))
    return result
  }, [screen.mono, screen.strong])
  const renderCell = useCallback(
    (row: ListRow, column: string) => {
      const rule = derived.find((candidate) => candidate.name === column)
      if (!rule) return undefined
      const value = row[column]
      if (typeof value !== 'number') return ''
      return (
        <span className="cell-flagged">
          {rule.flagWhenNotPositive && value <= 0 && (
            <span className="flag flag-danger">
              <Icon name="close" />
              {t(rule.flagWhenNotPositive)}
            </span>
          )}
          <span>{formatQuantity(value, locale)}</span>
        </span>
      )
    },
    [derived, locale, t],
  )

  const rows: ListRow[] = useMemo(() => (page.data?.rows ?? []).map(enrich), [enrich, page.data])
  const shownIndex = page.data?.index ?? 0
  const firstRow = rows.length === 0 ? 0 : shownIndex * pageSize + 1

  // ------------------------------------------------------------- представления

  const saveMutation = useMutation({
    mutationFn: ({ name, shared }: { name: string; shared: boolean }) =>
      createView(entity, name, definition, shared),
    onSuccess: (view) => {
      setActiveViewId(view.id)
      setAppliedDefinition(normalise(definition))
      void queryClient.invalidateQueries({ queryKey: ['views', entity] })
    },
    onError: (error) => setNotice((error as Error).message),
  })

  const updateMutation = useMutation({
    mutationFn: (id: string) => {
      const view = viewsQuery.data?.views.find((candidate) => candidate.id === id)
      return updateView(id, view?.name ?? '', definition)
    },
    onSuccess: () => {
      setAppliedDefinition(normalise(definition))
      void queryClient.invalidateQueries({ queryKey: ['views', entity] })
    },
    onError: (error) => setNotice((error as Error).message),
  })

  const deleteViewMutation = useMutation({
    mutationFn: deleteView,
    onSuccess: () => {
      setActiveViewId(null)
      void queryClient.invalidateQueries({ queryKey: ['views', entity] })
    },
    onError: (error) => setNotice((error as Error).message),
  })

  const defaultViewMutation = useMutation({
    mutationFn: makeViewDefault,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['views', entity] }),
    onError: (error) => setNotice((error as Error).message),
  })

  const applyView = useCallback(
    (view: SavedView) => {
      try {
        const parsed = JSON.parse(view.definition) as ListViewDefinition
        list.apply(parsed)
        setActiveViewId(view.id)
        // Запоминается нормализованный вид, а не исходный текст из базы: тот несёт свой
        // порядок ключей и отсутствующие поля, и сравнение с ним объявляло бы
        // представление изменённым сразу после применения.
        setAppliedDefinition(normalise(parsed))
      } catch {
        // Нечитаемое определение — это испорченная строка в базе, а не действие
        // пользователя. Молча оставить старый список хуже: он будет выглядеть как
        // применённое представление.
        setNotice(t('view.broken'))
      }
    },
    [list, t],
  )

  const isDirty = appliedDefinition !== null && appliedDefinition !== normalise(definition)
  const activeView = viewsQuery.data?.views.find((view) => view.id === activeViewId) ?? null

  // ------------------------------------------------------- выгрузка и действия

  const [exporting, setExporting] = useState(false)

  async function runExport(format: 'csv' | 'xlsx') {
    setExporting(true)
    setNotice(null)
    try {
      // The file is titled as the table is: the same labels, custom fields by their names.
      const columnHeaders = Object.fromEntries(
        (descriptor?.columns ?? [])
          .map((column) => [column.name, columnLabel(t, descriptor?.columns, column.name)] as const)
          .filter(([name, label]) => label !== `column.${name}`),
      )
      const blob = await exportList(entity, definition, format, columnHeaders)
      saveBlob(blob, `${entity.replace('.', '-')}.${format}`)
    } catch (error) {
      // «Слишком много строк» — это сообщение, а не пустой файл: пользователь должен
      // узнать, что надо сузить фильтр, а не решить, что выгрузка не работает.
      setNotice(error instanceof ApiError ? error.message : String(error))
    } finally {
      setExporting(false)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => bulkDelete(entity, ids),
    onSuccess: (result) => {
      setSelection(new Set())
      setConfirmDelete(false)
      // Затронуто может быть меньше выбранного: право на удаление бывает уже права на
      // чтение, которым нарисован список. Сообщать выбранное число значило бы соврать.
      setNotice(
        t('bulk.deleted', { affected: result.affected, requested: result.requested }),
      )
      void queryClient.invalidateQueries({ queryKey: ['list', entity] })
    },
    onError: (error) => {
      setConfirmDelete(false)
      setNotice((error as Error).message)
    },
  })

  // Строка ведёт в карточку только там, где карточка описана (#66).
  const openable = cardDescriptor(entity) !== null
  // Карточка живёт под путём своего списка (`/counterparties/:id`), поэтому префикс —
  // текущий путь, а не собранный из кода сущности.
  const cardPathPrefix = location.pathname.replace(/\/$/, '')

  const mayDelete = can(user, resource, 'delete')
  const mayUpdate = can(user, resource, 'update')
  const mayCreate = CREATABLE.has(entity) && can(user, resource, 'create')
  // Выгрузка — по праву чтения: сервер решает по нему же.
  const mayExport = can(user, resource, 'read')
  const maySegments = TAGGABLE.has(entity)

  // ----------------------------------------------------------------- группы

  const [expanded, setExpanded] = useState<{ key: string; open: Set<string> | null }>({
    key: '',
    open: null,
  })
  const groupKey = `${list.state.groupBy ?? ''}|${definitionKey}`
  const groups = (groupsQuery.data?.groups ?? []).slice(0, MAX_GROUPS)
  // Пока пользователь не раскрывал и не сворачивал группы, раскрыты первые: свёрнутый
  // список целиком выглядит пустым, раскрытый целиком — это снова одна длинная лента.
  const openGroups =
    expanded.key === groupKey && expanded.open
      ? expanded.open
      : new Set(groups.slice(0, OPEN_GROUPS).map((group) => String(group.value)))
  const toggleGroup = (value: string) => {
    const next = new Set(openGroups)
    if (!next.delete(value)) next.add(value)
    setExpanded({ key: groupKey, open: next })
  }

  // ------------------------------------------------------------------ отрисовка

  const chips = useMemo(
    () =>
      descriptor
        ? availableChips(entity, descriptor.columns, {
            employeeId: user?.employeeId ?? null,
            userId: user?.userId ?? null,
            today: localToday(),
          })
        : [],
    [descriptor, entity, user?.employeeId, user?.userId],
  )
  const search = descriptor ? searchColumn(entity, descriptor.columns) : null

  const head = (
    <div className="page-head">
      <h1 className="h1">{t(titleKey)}</h1>
      {descriptor && total.data && (
        <span className="muted t-md" data-testid="list-total">
          {t('list.count', { count: total.data.count.toLocaleString(locale) })}
        </span>
      )}
      {toolbar}
      <span className="spacer" />
      {/* Заведение записи — только там, где есть экран создания, и по праву `create`, а не
          `read`: право листать список не должно обещать возможность в него добавить. */}
      {mayCreate && descriptor && (
        <Link className="btn btn-primary" to={`${cardPathPrefix}/new`}>
          <Icon name="plus" />
          {t('list.create')}
        </Link>
      )}
    </div>
  )

  if (descriptorQuery.isLoading) {
    return (
      <section className="entity-list">
        {head}
        <TableSkeleton label={t('app.loading')} />
      </section>
    )
  }
  if (descriptorQuery.error || !descriptor) {
    const error = descriptorQuery.error
    const forbidden = error instanceof ApiError && error.isForbidden
    return (
      <section className="entity-list">
        {head}
        {forbidden ? (
          <StateBox
            kind="forbidden"
            title={t('list.state.forbidden', { section: t(titleKey) })}
            description={t('access.deniedHint')}
          />
        ) : (
          <StateBox
            kind="error"
            title={t('list.state.error')}
            description={t('list.state.errorHint')}
            code={errorCode(error)}
            actions={
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void descriptorQuery.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('list.retry')}
              </button>
            }
          />
        )}
      </section>
    )
  }

  const filterCount = list.state.filters.length
  const groupColumn = list.state.groupBy
    ? descriptor.columns.find((column) => column.name === list.state.groupBy)
    : undefined

  const toolbarRow = (
    <div className="toolbar list-toolbar" role="toolbar" aria-label={t('list.toolbar')}>
      <Popover
        label={t('view.picker')}
        open={viewsOpen}
        onOpenChange={setViewsOpen}
        buttonClassName="btn btn-secondary btn-sm"
        trigger={
          <>
            <Icon name="lines" size="sm" />
            {activeView ? activeView.name : t('view.none')}
            {isDirty && <span className="muted">· {t('view.modified')}</span>}
            <Icon name="chevron" size="xs" />
          </>
        }
      >
        <ViewPicker
          views={viewsQuery.data?.views ?? []}
          activeId={activeViewId}
          onApply={(view) => {
            applyView(view)
            setViewsOpen(false)
          }}
          onSave={(name, shared) => saveMutation.mutate({ name, shared })}
          onUpdate={(id) => updateMutation.mutate(id)}
          onDelete={(id) => deleteViewMutation.mutate(id)}
          onMakeDefault={(id) => defaultViewMutation.mutate(id)}
          isDirty={isDirty}
        />
      </Popover>

      {search && (
        <SearchBox
          column={search}
          descriptors={descriptor.columns}
          filters={list.state.filters}
          onChange={list.setFilters}
        />
      )}

      {chips.length > 0 && <div className="vsep" />}
      <QuickChips chips={chips} filters={list.state.filters} onChange={list.setFilters} />

      <Popover
        label={t('filter.title')}
        className="fbuilder-pop"
        trigger={
          <>
            <Icon name="filter" size="sm" />
            {t('filter.title')}
            {filterCount > 0 && <span className="count-pill">{filterCount}</span>}
          </>
        }
      >
        <FilterBuilder
          entity={entity}
          descriptors={descriptor.columns}
          filters={list.state.filters}
          onChange={list.setFilters}
        />
      </Popover>

      <span className="spacer" />

      <Popover
        label={t('list.groupBy')}
        align="end"
        trigger={
          <>
            {groupColumn
              ? t('list.groupByValue', { column: columnLabel(t, descriptor.columns, groupColumn.name) })
              : t('list.groupBy')}
            <Icon name="chevron" size="xs" />
          </>
        }
      >
        {(close) => (
          <div role="group" aria-label={t('list.groupBy')}>
            <button
              type="button"
              className={`menu-item${!list.state.groupBy ? ' is-active' : ''}`}
              aria-pressed={!list.state.groupBy}
              onClick={() => {
                list.setGroupBy(null)
                close()
              }}
            >
              {t('list.noGrouping')}
            </button>
            {descriptor.columns.filter(groupable).map((column) => (
              <button
                key={column.name}
                type="button"
                className={`menu-item${list.state.groupBy === column.name ? ' is-active' : ''}`}
                aria-pressed={list.state.groupBy === column.name}
                onClick={() => {
                  list.setGroupBy(column.name)
                  close()
                }}
              >
                {columnLabel(t, descriptor.columns, column.name)}
              </button>
            ))}
          </div>
        )}
      </Popover>

      <Popover label={t('list.columns')} iconOnly align="end" buttonClassName="btn btn-ghost btn-sm btn-icon" trigger={<Icon name="columns" size="sm" />}>
        <ColumnPicker
          descriptors={descriptor.columns}
          selected={definition.columns}
          onToggle={list.toggleColumn}
          onReset={() => list.setColumns([])}
        />
      </Popover>

      {(mayExport || importable || maySegments) && (
        <Popover
          label={t('list.moreActions')}
          iconOnly
          align="end"
          buttonClassName="btn btn-ghost btn-sm btn-icon"
          trigger={<Icon name="more" size="sm" className="i-dots" />}
        >
          {(close) => (
            <div role="menu" aria-label={t('list.moreActions')}>
              {mayExport && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  disabled={exporting}
                  onClick={() => {
                    close()
                    void runExport('xlsx')
                  }}
                >
                  <Icon name="download" />
                  {exporting ? t('export.running') : t('export.xlsx')}
                  {total.data && (
                    <span className="end t-xs muted">
                      {t('list.rows', { count: total.data.count.toLocaleString(locale) })}
                    </span>
                  )}
                </button>
              )}
              {mayExport && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  disabled={exporting}
                  onClick={() => {
                    close()
                    void runExport('csv')
                  }}
                >
                  <Icon name="file" />
                  {t('export.csv')}
                </button>
              )}
              {importable && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    close()
                    setTool('import')
                  }}
                >
                  <Icon name="upload" />
                  {t('list.importMenu')}
                </button>
              )}
              {maySegments && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    close()
                    setTool('segments')
                  }}
                >
                  <Icon name="segments" />
                  {t('list.segmentsMenu')}
                </button>
              )}
            </div>
          )}
        </Popover>
      )}
    </div>
  )

  const bulkBar = (
    <div className="bulk" role="toolbar" aria-label={t('bulk.label')}>
      <span className="sel">
        <input
          type="checkbox"
          checked
          aria-label={t('bulk.clear')}
          onChange={() => setSelection(new Set())}
        />
        {t('bulk.selected', { count: selection.size })}
      </span>
      {/* Пометка — это правка записи: право update, как у массовой правки колонки. */}
      {TAGGABLE.has(entity) && mayUpdate && (
        <TagBulkActions entity={entity} ids={[...selection]} onDone={setNotice} />
      )}
      {REASSIGNABLE.has(entity) && mayUpdate && (
        <SalesBulkActions entity={entity} selection={{ ids: [...selection] }} onDone={setNotice} />
      )}
      <span className="spacer" />
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelection(new Set())}>
        {t('bulk.clear')}
      </button>
      {/* Удаление — за чертой, красным только текстом и с подтверждением (дизайн: опасные
          действия). Без права delete кнопки нет: отказ после нажатия учит не верить кнопкам. */}
      {mayDelete && (
        <>
          <span className="vsep" />
          <button
            type="button"
            className="btn btn-danger-quiet btn-sm"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteMutation.isPending}
          >
            <Icon name="trash" size="sm" />
            {t('bulk.deleteAsk')}
          </button>
        </>
      )}
    </div>
  )

  const pageError = page.error ?? groupsQuery.error
  const showEmpty = !grouped && !page.isLoading && !page.error && rows.length === 0
  const groupsEmpty = grouped && groupsQuery.isSuccess && groups.length === 0

  let content: React.ReactNode
  if (pageError) {
    const forbidden = pageError instanceof ApiError && pageError.isForbidden
    content = forbidden ? (
      <StateBox
        kind="forbidden"
        title={t('list.state.forbidden', { section: t(titleKey) })}
        description={t('access.deniedHint')}
      />
    ) : (
      <StateBox
        kind="error"
        title={t('list.state.error')}
        description={pageError instanceof ApiError ? pageError.message : t('list.state.errorHint')}
        code={errorCode(pageError)}
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void (grouped ? groupsQuery.refetch() : page.refetch())}
          >
            <Icon name="refresh" size="sm" />
            {t('list.retry')}
          </button>
        }
      />
    )
  } else if (showEmpty || groupsEmpty) {
    content =
      filterCount > 0 ? (
        <StateBox
          kind="empty"
          title={t('list.state.noMatch')}
          description={t('list.state.noMatchHint')}
          actions={
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => list.setFilters([])}>
              {t('filter.clearAll')}
            </button>
          }
        />
      ) : (
        <StateBox
          kind="empty"
          title={t('list.state.empty')}
          description={t('list.state.emptyHint')}
          actions={
            mayCreate ? (
              <Link className="btn btn-secondary btn-sm" to={`${cardPathPrefix}/new`}>
                {t('list.create')}
              </Link>
            ) : undefined
          }
        />
      )
  } else if ((!grouped && page.isLoading) || (grouped && groupsQuery.isLoading)) {
    content = <TableSkeleton label={t('app.loading')} />
  } else {
    // `columns` — то, что представление просило показать (и колонки, считаемые на клиенте).
    content = (
      <DataTable
        entity={entity}
        columns={columns}
        descriptors={descriptors}
        rows={grouped ? [] : rows}
        sort={list.state.sort}
        onToggleSort={list.toggleSort}
        selection={selection}
        onSelectionChange={setSelection}
        isLoading={page.isLoading}
        onOpenRow={openable ? (id) => navigate(`${cardPathPrefix}/${id}`) : undefined}
        columnClass={columnClass}
        renderCell={renderCell}
        renderBody={
          grouped && list.state.groupBy
            ? (renderRow, columnCount) => (
                <>
                  {groups.map((group) => (
                    <GroupRows
                      key={`${definitionKey}|${String(group.value)}`}
                      entity={entity}
                      definition={definition}
                      column={list.state.groupBy as string}
                      descriptor={groupColumn}
                      group={group}
                      expanded={openGroups.has(String(group.value))}
                      onToggle={() => toggleGroup(String(group.value))}
                      renderRow={renderRow}
                      columnCount={columnCount}
                      enrich={enrich}
                    />
                  ))}
                  {(groupsQuery.data?.groups.length ?? 0) > MAX_GROUPS && (
                    <tr>
                      <td colSpan={columnCount} className="muted t-sm">
                        {t('list.groupsCapped', { shown: MAX_GROUPS })}
                      </td>
                    </tr>
                  )}
                </>
              )
            : undefined
        }
      />
    )
  }

  return (
    <section className="entity-list">
      {head}

      {selection.size > 0 ? bulkBar : toolbarRow}

      <FilterTokens
        entity={entity}
        descriptors={descriptor.columns}
        filters={list.state.filters}
        chips={chips}
        onChange={list.setFilters}
        extra={
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewsOpen(true)}>
            {t('view.saveCurrent')}
          </button>
        }
      />

      {tool && (
        <div className="panel list-tool">
          <div className="panel-h">
            <span className="h3">{t(tool === 'import' ? 'import.title' : 'segments.title')}</span>
            <span className="spacer" />
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon"
              aria-label={t('list.closePanel')}
              onClick={() => setTool(null)}
            >
              <Icon name="close" size="sm" />
            </button>
          </div>
          <div className="panel-b">
            {tool === 'import' ? (
              <ImportPanel
                target={entity}
                open
                onImported={() => void queryClient.invalidateQueries({ queryKey: ['list', entity] })}
              />
            ) : (
              <SegmentBar entity={entity} filters={list.state.filters} onApply={list.setFilters} />
            )}
          </div>
        </div>
      )}

      {/* «Все по фильтру» (#107): двести сделок уволившегося — это фильтр, а не двести
          отмеченных строк. Только при непустом фильтре: действие над всем списком без
          фильтра — не то, что делают одной кнопкой. */}
      {REASSIGNABLE.has(entity) && mayUpdate && filterCount > 0 && (
        <FilterActions entity={entity} definition={definition} onDone={setNotice} />
      )}

      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      {content}

      {!grouped && !pageError && rows.length > 0 && (
        <footer className="list-footer">
          <Pager
            from={firstRow}
            to={firstRow === 0 ? 0 : firstRow + rows.length - 1}
            total={total.data?.count ?? null}
            hasPrev={stack.length > 1}
            // Курсор приходит только когда следующая страница есть (#48): на последней его
            // нет, и кнопка гаснет без отдельного счётчика.
            hasNext={Boolean(page.data?.hasMore && page.data.nextCursor)}
            busy={page.isFetching}
            onPrev={() => setPaging({ key: definitionKey, stack: stack.slice(0, -1) })}
            onNext={() => {
              const next = page.data?.nextCursor
              if (next) setPaging({ key: definitionKey, stack: [...stack, next] })
            }}
            pageSize={pageSize}
            onPageSize={list.setPageSize}
          />
        </footer>
      )}

      {confirmDelete && (
        <ConfirmDelete
          count={selection.size}
          pending={deleteMutation.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => deleteMutation.mutate([...selection])}
        />
      )}
    </section>
  )
}

/** Код ответа для поддержки: `403 · access.denied`. */
function errorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  return [String(error.status), error.code].filter(Boolean).join(' · ')
}

/** Скелет таблицы на время загрузки — пустая таблица на полсекунды выглядит как «данных нет». */
function TableSkeleton({ label }: { label: string }) {
  return (
    <div className="tbl-wrap" aria-busy="true" aria-label={label} role="status">
      <table className="tbl">
        <tbody>
          {[60, 45, 70, 50, 65].map((width, index) => (
            <tr key={index}>
              <td className="chk">
                <span className="skel" style={{ width: 14, height: 14 }} />
              </td>
              <td>
                <span className="skel" style={{ width: `${width}%` }} />
              </td>
              <td>
                <span className="skel" style={{ width: 90 }} />
              </td>
              <td>
                <span className="skel" style={{ width: 64, marginLeft: 'auto' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Подтверждение удаления (дизайн: опасное действие — модалка, фокус на «Отмена»).
 *
 * Фокус по умолчанию на отмене: Enter после случайного нажатия «Удалить…» не должен
 * удалять сорок записей.
 */
function ConfirmDelete({
  count,
  pending,
  onCancel,
  onConfirm,
}: {
  count: number
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useApp()
  return (
    <div
      className="scrim list-scrim"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel()
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={t('bulk.confirmTitle', { count })}>
        <div className="modal-h">
          <span className="modal-ico">
            <Icon name="trash" />
          </span>
          <span className="h3">{t('bulk.confirmTitle', { count })}</span>
        </div>
        <div className="modal-b">{t('bulk.confirmText')}</div>
        <div className="modal-f">
          <button type="button" className="btn btn-secondary" autoFocus onClick={onCancel}>
            {t('card.cancel')}
          </button>
          <button type="button" className="btn btn-danger" disabled={pending} onClick={onConfirm}>
            {t('bulk.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Приводит определение к сравнимому виду.
 *
 * Нужно для одного вопроса: изменил ли пользователь применённое представление. Сравнивать
 * сырой `JSON.stringify` нельзя — определение из базы несёт свой порядок ключей и не имеет
 * полей, которые интерфейс подставляет по умолчанию, поэтому кнопка «Обновить» загоралась
 * бы сразу после применения представления и не значила бы ничего.
 */
export function normalise(definition: ListViewDefinition): string {
  return JSON.stringify({
    columns: definition.columns ?? [],
    filters: definition.filters ?? [],
    sort: definition.sort ?? [],
    groupBy: definition.groupBy ?? null,
    pageSize: definition.pageSize ?? null,
  })
}

/** Действия над всеми записями под текущим фильтром — с числом, чтобы было видно, сколько. */
function FilterActions({
  entity,
  definition,
  onDone,
}: {
  entity: string
  definition: ListViewDefinition
  onDone: (message: string) => void
}) {
  const { t } = useApp()
  const count = useQuery({
    queryKey: ['list-count', entity, definition.filters],
    queryFn: ({ signal }) => fetchCount(entity, definition, signal),
    retry: false,
  })
  return (
    <details className="filter-actions">
      <summary>{t('bulk.byFilter', { count: count.data?.count ?? '…' })}</summary>
      <SalesBulkActions entity={entity} selection={{ filters: definition.filters }} onDone={onDone} />
    </details>
  )
}
